<template>
  <div
    :style="{
      minHeight: canvasPixelHeight,
      height: canvasPixelHeight,
      width: canvasPixelHeight,
      minWidth: canvasPixelWidth,
    }"
    class="tw:w-full tw:rounded-base tw:overflow-hidden"
    :class="{ 'tw:invisible': !isImageParsed }"
  >
    <!-- <div class="tw:flexcenter tw:rounded-base tw:overflow-hidden"> -->
    <cropper-canvas ref="cropper_canvas" scale-step="0">
      <cropper-image ref="cropper_image" scalable translatable :src="imageSrc"></cropper-image>
      <cropper-shade ref="cropper_shade" hidden centered :class="{ 'tw:rounded-full': isRounded }"> </cropper-shade>
      <cropper-handle ref="cropper_handle" plain></cropper-handle>
      <cropper-selection
        ref="cropper_selection"
        initial-coverage="0.8"
        :initial-aspect-ratio="aspectRatio"
        centered
        :class="{ 'tw:rounded-full': isRounded }"
        class="tw:inset-ring tw:inset-ring-white/40"
      >
        <div
          class="tw:absolute tw:overflow-hidden tw:top-0 tw:left-0 tw:w-full tw:h-full tw:pointer-events-none"
          :class="{ 'tw:rounded-full': isRounded }"
        >
          <div class="tw:absolute tw:top-0 tw:left-1/3 tw:w-[1px] tw:h-full tw:bg-white/40"></div>
          <div class="tw:absolute tw:top-0 tw:left-2/3 tw:w-[1px] tw:h-full tw:bg-white/40"></div>
          <div class="tw:absolute tw:left-0 tw:top-1/3 tw:h-[1px] tw:w-full tw:bg-white/40"></div>
          <div class="tw:absolute tw:left-0 tw:top-2/3 tw:h-[1px] tw:w-full tw:bg-white/40"></div>
        </div>
        <cropper-handle ref="cropper_handle" action="move" plain :class="{ 'tw:rounded-full': isRounded }">
        </cropper-handle>
      </cropper-selection>
    </cropper-canvas>
    <!-- </div> -->
  </div>
</template>

<script>
//eslint-disable-next-line
import Cropper from 'cropperjs';

export default {
  name: 'CropperWrapper',
  props: {
    x: {
      type: Number,
    },
    y: {
      type: Number,
    },
    circle: {
      type: Boolean,
      default: false,
    },
    canvasWidth: {
      type: Number,
      default: 400,
    },
    imageSrc: {
      type: String,
      required: true,
    },
    zoomStep: {
      type: Number,
      default: 0.025,
    },
    zoomSteps: {
      type: Number,
      default: 20,
    },
  },
  data: () => ({
    minZoom: 0,
    isImageParsed: false,
  }),
  computed: {
    aspectRatio() {
      return this.x / this.y;
    },
    canvasHeight() {
      const multiplier = this.x / this.canvasWidth;
      return this.y / multiplier;
    },
    canvasPixelWidth() {
      return this.canvasWidth + 'px';
    },
    canvasPixelHeight() {
      return this.canvasHeight + 'px';
    },
    isRounded() {
      return this.circle && this.x === this.y;
    },
  },
  methods: {
    async generateImage(options) {
      if (!this.$refs.cropper_canvas) return;
      const result = await this.$refs.cropper_selection.$toCanvas(options);
      return result;
    },
    onZoomChanged(e) {
      const image = this.$refs.cropper_image;
      let computedTransform = image.$getTransform();
      const newZoom = this.minZoom + this.zoomStep * e;
      computedTransform[0] = computedTransform[3] = newZoom;
      image.$setTransform(computedTransform);
    },
    exceededAxis(selection, maxSelection) {
      let stack = [];
      if (selection.x < parseFloat(maxSelection.x.toFixed(5))) stack.push('-x');
      if (selection.y < parseFloat(maxSelection.y.toFixed(5))) stack.push('-y');
      if (selection.x + selection.width > maxSelection.x + maxSelection.width) stack.push('+x');
      if (selection.y + selection.height > maxSelection.y + maxSelection.height) stack.push('+y');
      return stack;
    },
    transformEvent(event) {
      const cropperCanvas = this.$refs.cropper_canvas;
      const cropperImage = this.$refs.cropper_image;
      const cropperSelection = this.$refs.cropper_selection;

      /** Creating clone to compute new position to check if it is valid */
      const cropperImageClone = cropperImage.cloneNode();
      cropperImageClone.style.transform = `matrix(${event.detail.matrix.join(', ')})`;
      cropperImageClone.style.opacity = '0';
      cropperCanvas.appendChild(cropperImageClone);
      const cropperImageRect = cropperImageClone.getBoundingClientRect();
      cropperCanvas.removeChild(cropperImageClone);
      /** */

      const cropperCanvasRect = cropperCanvas.getBoundingClientRect();

      const selection = cropperSelection;
      const maxSelection = {
        x: cropperImageRect.left - cropperCanvasRect.left,
        y: cropperImageRect.top - cropperCanvasRect.top,
        width: cropperImageRect.width,
        height: cropperImageRect.height,
      };
      const exceededAxisArray = this.exceededAxis(selection, maxSelection);
      if (exceededAxisArray.length > 0) {
        if (exceededAxisArray.length < 4) {
          /** Correct image position on zooming out */
          const matrixX = event.detail.matrix[4];
          const matrixY = event.detail.matrix[5];
          const oldMatrixZoom = event.detail.oldMatrix[0];
          const matrixZoom = event.detail.matrix[0];
          const newZoom = matrixZoom - oldMatrixZoom;
          if (newZoom !== 0 && matrixZoom < oldMatrixZoom) {
            const maxSelectionWidth = maxSelection.x + maxSelection.width;
            const selectionWidth = selection.x + selection.width;
            const maxSelectionHeight = maxSelection.y + maxSelection.height;
            const selectionHeight = selection.y + selection.height;
            const newMatrix = [
              matrixZoom,
              0,
              0,
              matrixZoom,
              exceededAxisArray.includes('-x')
                ? matrixX - (maxSelection.x - selection.x)
                : exceededAxisArray.includes('+x')
                  ? matrixX - (maxSelectionWidth - selectionWidth)
                  : matrixX,
              exceededAxisArray.includes('-y')
                ? matrixY - (maxSelection.y - selection.y)
                : exceededAxisArray.includes('+y')
                  ? matrixY - (maxSelectionHeight - selectionHeight)
                  : matrixY,
            ];
            cropperImage.$setTransform(...newMatrix);
          } else if (newZoom === 0) {
            /** Allow smooth panning on edges */
            if (exceededAxisArray.includes('-x') || exceededAxisArray.includes('+x'))
              cropperImage.$move(0, matrixY - event.detail.oldMatrix[5]);
            else if (exceededAxisArray.includes('-y') || exceededAxisArray.includes('+y'))
              cropperImage.$move(matrixX - event.detail.oldMatrix[4], 0);
          }
        }
        event.preventDefault();
      }
    },
    async initImage() {
      const selection = this.$refs.cropper_selection;
      const image = this.$refs.cropper_image;
      image.removeEventListener('transform', this.transformEvent);
      const centeredImage = image.$center();
      const imageSize = centeredImage.getBoundingClientRect();
      const heightDivider = Math.ceil(selection.height + 0.01) / Math.floor(imageSize.height);
      const widthDivider = Math.ceil(selection.width + 0.01) / Math.floor(imageSize.width);
      const newScale = heightDivider > widthDivider ? heightDivider : widthDivider;
      const scaledImage = centeredImage.$scale(newScale);
      this.minZoom = scaledImage.$getTransform()[0];
      this.isImageParsed = true;
      await this.$nextTick();
      scaledImage.addEventListener('transform', this.transformEvent);
    },
    async initCropper() {
      await this.$nextTick();
      this.$refs.cropper_image.$ready(() => {
        this.initImage();
      });
    },
    destroyCropper() {
      this.isImageParsed = false;
      const image = this.$refs.cropper_image;
      if (!image) return;
      image.removeEventListener('transform', this.transformEvent);
    },
  },
  mounted() {
    this.initCropper();
  },
  beforeDestroy() {
    this.destroyCropper();
  },
};
</script>

<style scoped>
:deep(cropper-canvas) {
  height: calc(v-bind(canvasPixelHeight));
  min-height: calc(v-bind(canvasPixelHeight));
  max-height: calc(v-bind(canvasPixelHeight));
  width: calc(v-bind(canvasPixelWidth));
  min-width: calc(v-bind(canvasPixelWidth));
  max-width: calc(v-bind(canvasPixelWidth));
}
</style>
